import { useActiveServices } from '@commenda/api';
import {
  Avatar,
  AvatarGroup,
  Badge,
  CountryCode,
  DataTable,
  FlagComponent,
  Icons,
  Skeleton,
  Tooltip,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { GetAllServicesForCompanyResponseType } from '@commenda/types/src/company/types';
import { Country } from 'country-state-city';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { onClickActiveService } from './ActiveServices';

type Service = GetAllServicesForCompanyResponseType['services'][number];

const STATUS_DISPLAY: Record<string, { label: string; variant: string }> = {
  NotStarted: { label: 'Not Started', variant: 'gray' },
  InProgress: { label: 'In Progress', variant: 'blue' },
  Completed: { label: 'Completed', variant: 'success' },
};

export const ActiveServicesTable = ({ companyId }: { companyId: number }) => {
  const router = useRouter();
  const { data: activeServices } = useActiveServices(companyId);
  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[]>
  >({});

  const services = useMemo(() => {
    if (!activeServices?.services) return [];
    return activeServices.services
      .filter((s) => s.status !== 'Completed')
      .map((s) => ({
        ...s,
        entityName: s.entity?.name ?? '',
      }));
  }, [activeServices?.services]);

  type ServiceWithEntityName = (typeof services)[number];

  // Build entity filter options from data
  const entityFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    let hasNoEntity = false;
    services.forEach((s) => {
      if (s.entity?.name) {
        if (!seen.has(s.entity.name)) {
          seen.set(s.entity.name, s.entity.name);
        }
      } else {
        hasNoEntity = true;
      }
    });
    const options = Array.from(seen.entries()).map(([value, label]) => ({
      value,
      label,
    }));
    if (hasNoEntity) {
      options.push({ value: '', label: 'No entity' });
    }
    return options;
  }, [services]);

  const columnGroups = useMemo(
    () => [
      {
        id: 'active-services',
        columns: [
          {
            id: 'title',
            accessorKey: 'title',
            header: 'Service name',
            title: 'Service name',
            cell: (cellCtx: { row: { original: Service } }) => (
              <span className="font-mulish text-sm font-normal leading-5 text-text-primary">
                {cellCtx.row.original.title}
              </span>
            ),
          },
          {
            id: 'entity',
            accessorKey: 'entity',
            accessorFn: (row: Service) => row.entity?.name ?? '',
            header: 'Entity',
            title: 'Entity',
            cell: (cellCtx: { row: { original: Service } }) => {
              const { entity, country, title } = cellCtx.row.original;
              const isIncorporation = title
                ?.toLowerCase()
                .includes('incorporation');
              return (
                <div className="flex items-center gap-md">
                  {country && (
                    <FlagComponent
                      _className="size-5 shrink-0"
                      country={country as CountryCode}
                      countryName={
                        Country.getCountryByCode(country as string)?.name
                      }
                    />
                  )}
                  {entity?.name ? (
                    <span className="font-mulish text-sm font-normal leading-5 text-text-primary">
                      {entity.name}
                    </span>
                  ) : isIncorporation ? (
                    <Tooltip content="Incorporation in progress">
                      <span className="font-mulish text-sm font-normal leading-5 italic text-text-disabled">
                        New {country} Entity
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="text-text-disabled">—</span>
                  )}
                </div>
              );
            },
          },
          {
            id: 'status',
            accessorKey: 'status',
            header: 'Status',
            title: 'Status',
            cell: (cellCtx: { row: { original: Service } }) => {
              const { original } = cellCtx.row;
              const status = STATUS_DISPLAY[original.status] ?? {
                label: original.status,
                variant: 'default',
              };
              return (
                <Badge variant={status.variant as any} size="sm">
                  {status.label}
                </Badge>
              );
            },
          },
          {
            id: 'pendingOn',
            accessorKey: 'pendingOn',
            header: 'Current assignee',
            title: 'Current assignee',
            cell: (cellCtx: { row: { original: Service } }) => {
              const { pendingOn } = cellCtx.row.original;
              if (pendingOn.length === 0) {
                return (
                  <span className="txt-sm-regular text-text-disabled">
                    Not yet assigned
                  </span>
                );
              }
              if (pendingOn.length === 1) {
                const { name, email } = pendingOn[0];

                if (name === null) {
                  return (
                    <div className="size-6 rounded-full bg-white border border-border-secondary flex items-center justify-center overflow-hidden">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src="/commenda-new-logo.svg"
                        alt="Commenda Logo"
                        className="size-4 object-contain"
                      />
                    </div>
                  );
                }

                return (
                  <div className="flex items-center gap-md">
                    <Avatar size="xs" name={name} colorSeed={email ?? name} />
                    <span className="font-mulish text-sm font-normal leading-5 text-text-primary">
                      {name}
                    </span>
                  </div>
                );
              }
              return (
                <AvatarGroup
                  size="xs"
                  avatars={pendingOn.map(({ name, email }) => ({
                    name,
                    colorSeed: email ?? name,
                    tooltip: { content: email ? `${name} (${email})` : name },
                  }))}
                />
              );
            },
          },
        ],
      },
    ],
    []
  );

  if (!activeServices) {
    return (
      <div className="flex flex-col gap-4xl p-3xl">
        <Skeleton className="h-6 w-48" />
        <div className="flex flex-col gap-md">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4xl p-3xl flex-1">
      <span className="font-mulish text-md font-bold leading-6 text-text-primary">
        Active Services
      </span>
      <DataTable
        columnGroups={columnGroups}
        data={services}
        onRowClick={(service: Service) => {
          mixpanel.track(MIXPANEL_EVENTS.HOME_ACTIVE_SERVICE_CLICKED, {
            surface_area: MIXPANEL_SURFACE_AREA.CLIENT_HOME,
            service_id: service.id,
            service_type: service.type,
          });
          onClickActiveService(service, router);
        }}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search',
          columnsToEnableSearchFor: ['title', 'entity'],
        }}
        filterConfig={{
          filters: [
            {
              columnId: 'entityName',
              type: 'MULTI_SELECT',
              label: 'Entity',
              icon: <Icons.Building06 className="size-4" />,
              options: entityFilterOptions,
            },
            {
              columnId: 'status',
              type: 'MULTI_SELECT',
              label: 'Status',
              icon: <Icons.CheckCircle className="size-4" />,
              options: [
                { value: 'NotStarted', label: 'Not Started' },
                { value: 'InProgress', label: 'In Progress' },
              ],
            },
          ],
          activeFilters,
          onFilterChange: setActiveFilters,
        }}
        emptyStateConfig={{
          title: 'No active services',
          description: 'Any active services will appear here.',
        }}
        tableRowClassName="hover:bg-bg-secondary cursor-pointer"
      />
    </div>
  );
};
